<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref, watch } from 'vue';
import router from '@/router';

import { useAuthStore } from '@/stores/authStore.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import UserService from '@/services/user/userService.ts';
import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import DeleteUserComponent from '@/components/user/DeleteUserComponent.vue';
import { formatItemValue, formatKycState, formatKycStatus, formatUserStatus } from '@/utils/formatters.ts';

import type { DeleteUserRequest, User, UserSortKey, UserStatusKey } from '@/types/services/user/user.type.ts';
import type { PaginatedQuery, SortItem, SortOrder } from '@/types/services/utils/request.type.ts';
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type { DataTableHeader } from 'vuetify/framework';
import type { ModalOptions } from '@/types/stores/modalStore.type.ts';

const { t } = useI18n()
const authStore = useAuthStore()

const users = ref<User[]>([])
const loading = ref<boolean>(false)

const search = ref<string>('')
const sort = ref<UserSortKey>('email')
const order = ref<SortOrder>('asc')
const limit = ref<number>(10)
const page = ref<number>(1)

const total = ref<number>(0)
const maxPages = ref<number>(1)

const headers = computed<DataTableHeader[]>(() => [
  {
    title: t('common.userList.header.email'),
    key: 'email',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.firstName'),
    key: 'firstName',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.lastName'),
    key: 'lastName',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.role'),
    key: 'role',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.status'),
    key: 'status',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.userList.kyc_state'),
    key: 'kyc_state',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.userList.kyc_status'),
    key: 'kyc_status',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.actions'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  },
])

onMounted(fetchUsers);

watch([search, sort, order, limit, page], fetchUsers);

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission);
}

async function fetchUsers(): Promise<void> {
  try {
    loading.value = true;

    const params: PaginatedQuery = {
      search: search.value,
      sort: sort.value,
      order: order.value,
      limit: limit.value,
      page: page.value
    };

    const response: PaginatedResponse<User> = await UserService.getPaginatedUser(params);

    users.value = response.data;
    total.value = response.total;
    maxPages.value = response.maxPages;
  } finally {
    loading.value = false;
  }
}

function onSort(sortBy: SortItem<UserSortKey>[]): void {
  const first = sortBy?.[0];
  sort.value = first.key;
  order.value = first.order;
}

async function onRowClick(_: MouseEvent, row: { item: User }): Promise<void> {
  if (hasPermission(['GET_KYC'])) {
    openUserDetail(row.item)
  }
}

function openUserDetail(user: User) {
  router.push(`/users/${user.id}/detail`)
}

function formatRoleValue(value: string): string {
  return value ? t(`common.role.${value}`) : t('common.noData');
}

async function updateStatus(user: User, status: UserStatusKey): Promise<void> {
  const username: string = `${user.firstName} ${user.lastName}`

  let title: string = t('common.userList.reactivateUser.title')
  let message: string = t('common.userList.reactivateUser.message', { username: username })
  let success: string = t('common.userList.reactivateSuccess')

  if (status == 'inactive') {
    title = t('common.userList.deactivateUser.title')
    message = t('common.userList.deactivateUser.message', { username: username })
    success = t('common.userList.deactivateSuccess')
  }

  const options: ModalOptions = { title, message }
  const modal = await displayModal(options)

  if (modal) {
    await UserService.updateUserStatus(user.id, status)
    displayToast(success, 'success')
    await fetchUsers()
  }
}

async function deleteUser(user: User): Promise<void> {
  const modal = await displayModal({
    component: DeleteUserComponent,
    props: {
      user: user
    },
  });

  if (modal && typeof modal === 'object') {
    const { reason } = modal as DeleteUserRequest;
    await UserService.anonymize(user.id, reason);
    displayToast(t('common.userList.deleteSuccess'), 'success');
    await fetchUsers();
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent icon="PhUsers">
    {{ t("views.userList.title") }}
  </MainTitleComponent>

  <v-row justify="space-between" align="center">
    <v-col cols="12" sm="5">
      <v-text-field
        v-model="search"
        :label="t('views.userList.search')"
        append-inner-icon="PhMagnifyingGlass"
        density="compact"
        hide-details
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="users"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        :sort-by="[{ key: sort, order: order }]"
        hover
        must-sort
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
        @update:sort-by="onSort"
        @click:row="onRowClick"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.lastname`]="{ item }">
          {{ formatItemValue(item.lastName) }}
        </template>
        <template #[`item.firstname`]="{ item }">
          {{ formatItemValue(item.firstName) }}
        </template>
        <template #[`item.email`]="{ item }">
          {{ formatItemValue(item.email) }}
        </template>
        <template #[`item.role`]="{ item }">
          {{ formatRoleValue(item.role) }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            :color="formatUserStatus(item.status)"
            text-color="white"
            class="ma-2"
          >
            {{ t('common.userList.status.' + item.status) }}
          </v-chip>
        </template>
        <template #[`item.kyc_state`]="{ item }">
          <v-chip
              :color="formatKycState(item.kyc?.state)"
              text-color="white"
              class="ma-2"
              v-if="item.kyc?.state"
          >
            {{ t('views.userList.kyc.state.' + item.kyc?.state) }}
          </v-chip>
          <span v-else>{{ t('common.noData') }}</span>
        </template>

        <template #[`item.kyc_status`]="{ item }">
          <v-chip
              :color="formatKycStatus(item.kyc?.status)"
              text-color="white"
              class="ma-2"
              v-if="item.kyc?.status"
          >
            {{ t('views.userList.kyc.status.' + item.kyc?.status) }}
          </v-chip>
          <span v-else>{{ t('common.noData') }}</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-menu
            v-if="hasPermission(['GET_KYC', 'DELETE_USER', 'ANONYMIZE_USER'])"
          >
            <template #activator="{ props }">
              <button type="button" v-bind="props">
                <v-icon icon="PhDotsThreeVertical" />
              </button>
            </template>

            <v-list>
              <v-list-item
                v-if="hasPermission(['GET_KYC'])"
                @click.prevent="openUserDetail(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.updateUser') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhPencil" />
                </template>
              </v-list-item>
              <v-list-item
                v-if="item.status == 'active' && hasPermission(['DELETE_USER'])"
                @click.prevent="updateStatus(item, 'inactive')"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.deactivateUser') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhProhibit" />
                </template>
              </v-list-item>
              <v-list-item
                v-if="item.status == 'inactive' && hasPermission(['DELETE_USER'])"
                @click.prevent="updateStatus(item, 'active')"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.reactivateUser') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhCheckCircle" />
                </template>
              </v-list-item>
              <v-list-item
                v-if="item.status == 'inactive' && hasPermission(['ANONYMIZE_USER'])"
                @click.prevent="deleteUser(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.deleteUser') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhTrash" />
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>
